/**
 * BeezFy Premium Cart System
 * Professional Shopify Cart Management with Drawer
 *
 * Features:
 * - AJAX cart operations without page reload
 * - Section rendering for dynamic updates
 * - Drawer slide animation
 * - Real-time cart count updates
 * - Integration with centralized systems
 * - Mobile optimized
 * - Performance focused
 *
 * @version 1.0.0
 * @author BeezFy Development Team
 */
// Miniature: chiede al CDN Shopify la larghezza giusta invece della foto originale
// (le originali da ~20 megapixel mandano in crash Safari su iPhone).
window.bfyImg = window.bfyImg || function (u, w) {
  if (!u) return '';
  u = String(u);
  if (u.indexOf('cdn.shopify.com') < 0 && u.indexOf('/cdn/shop/') < 0) return u;
  u = u.replace(/([?&])width=[0-9]+(&|$)/, function (m, a, b) { return b ? a : ''; }).replace(/[?&]$/, '');
  return u + (u.indexOf('?') > -1 ? '&' : '?') + 'width=' + w;
};


class BeezfyPremiumCart {
  constructor() {
    this.selectors = {
      drawer: '#beezfy-cart-drawer',
      overlay: '.beezfy-drawer-overlay',
      cartCount: '[data-cart-count], .cart-count',
      cartIcon: '[data-cart-icon], .header__icon--cart',
      cartTotal: '[data-cart-total]',
      cartItems: '[data-cart-items]',
      cartEmpty: '[data-cart-empty]',
      cartFooter: '[data-cart-footer]',
      closeBtn: '[data-drawer-close]',
      checkoutBtn: '[data-checkout-btn]',
      itemRemove: '[data-item-remove]',
      itemQuantity: '[data-item-quantity]',
      quantityBtn: '[data-quantity-btn]'
    };

    this.state = {
      isOpen: false,
      isLoading: false,
      cart: null,
      refreshTimer: null
    };

    this.config = {
      animationDuration: 300,
      debounceDelay: 250,
      sectionId: 'cart-drawer',
      routes: {
        cart: '/cart.js',
        add: '/cart/add.js',
        update: '/cart/update.js',
        change: '/cart/change.js',
        clear: '/cart/clear.js'
      }
    };

    this.init();
  }

  /**
   * Initialize cart system
   */
  async init() {
    // Setup DOM elements
    this.setupDrawer();

    // Bind events
    this.bindEvents();

    // Setup AJAX interception for cart operations
    this.interceptCartRequests();

    // Fetch initial cart state
    await this.fetchCart();

    // Preload recommendations in background (don't wait)
    this.loadRecommendations().catch(err => {});

    // Mark as initialized
    this.initialized = true;

    // Guardia bfcache: quando una pagina viene ripristinata dal tasto "Indietro" (snapshot
    // congelato dal browser), ri-sincronizza il carrello dal server così il contatore header e
    // il drawer non restano mai su uno stato vecchio/misto. La pagina /cart ha un reload dedicato.
    window.addEventListener('pageshow', (e) => {
      if (!e.persisted) return;
      this.fetchCart().then(() => {
        this.updateHeaderCart();
        if (this.state.isOpen) { this.updateUI(); this.renderCartItems(); }
      }).catch(() => {});
    });

    // Dispatch ready event
    document.dispatchEvent(new CustomEvent('beezfy:cart:ready', {
      detail: { cart: this.state.cart }
    }));
  }

  /**
   * Intercept all cart AJAX requests (both XMLHttpRequest and Fetch)
   */
  interceptCartRequests() {
    const self = this;

    // LTD: apri il cart drawer in automatico SOLO se l'azione post-ATC scelta in admin è "drawer".
    // Con "popup" (default), il post-ATC è gestito dal popup premium: evitiamo la doppia UI (drawer + popup).
    const shouldAutoOpenDrawer = () =>
      (window.BeezfyTheme && window.BeezfyTheme.settings && window.BeezfyTheme.settings.atc_action_type
        ? window.BeezfyTheme.settings.atc_action_type
        : 'popup') === 'drawer';

    // Method 1: Override XMLHttpRequest
    const originalXHROpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function() {
      const url = arguments[1];
      const method = arguments[0];

      // Listen for cart URLs
      if (method.toUpperCase() === 'POST' && url && url.includes('/cart/')) {
        this.addEventListener('load', function() {
          if (this.status === 200) {
  
            // Small delay to ensure cart is updated
            setTimeout(() => {
              self.fetchCart().then(() => {
                self.updateHeaderCart();

                // Open drawer if it was an add (solo se l'azione admin è "drawer")
                if (url.includes('/cart/add') && shouldAutoOpenDrawer()) {
                  self.open();
                }
              });
            }, 100);
          }
        });
      }

      return originalXHROpen.apply(this, arguments);
    };

    // Method 2: Override Fetch API
    const originalFetch = window.fetch;
    window.fetch = function(...args) {
      const url = args[0];
      const options = args[1] || {};

      // Check if it's a cart request
      if (options.method === 'POST' && url && url.includes('/cart/')) {
        return originalFetch.apply(this, args).then(response => {
          if (response.ok) {

            // Clone response before consuming
            const responseClone = response.clone();

            // Small delay to ensure cart is updated
            setTimeout(() => {
              self.fetchCart().then(() => {
                self.updateHeaderCart();

                // Open drawer if it was an add (solo se l'azione admin è "drawer")
                if (url.includes('/cart/add') && shouldAutoOpenDrawer()) {
                  self.open();
                }
              });
            }, 100);
          }
          return response;
        });
      }

      return originalFetch.apply(this, args);
    };
  }

  /**
   * Setup drawer DOM structure
   */
  setupDrawer() {
    // Check if drawer exists
    let drawer = document.querySelector(this.selectors.drawer);

    if (!drawer) {
      // Create drawer structure
      drawer = document.createElement('div');
      drawer.id = 'beezfy-cart-drawer';
      drawer.className = 'beezfy-drawer beezfy-drawer--cart';
      drawer.setAttribute('data-beezfy-drawer', 'cart');
      drawer.setAttribute('aria-hidden', 'true');
      drawer.setAttribute('role', 'dialog');
      drawer.setAttribute('aria-label', 'Shopping Cart');

      drawer.innerHTML = this.getDrawerTemplate();
      document.body.appendChild(drawer);
    }

    // Create overlay if needed
    let overlay = document.querySelector(this.selectors.overlay);
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.className = 'beezfy-drawer-overlay';
      overlay.setAttribute('data-drawer-overlay', '');
      document.body.appendChild(overlay);
    }

    // Cache elements
    this.elements = {
      drawer,
      overlay,
      body: drawer.querySelector('[data-cart-body]'),
      items: drawer.querySelector('[data-cart-items]'),
      footer: drawer.querySelector('[data-cart-footer]'),
      empty: drawer.querySelector('[data-cart-empty]')
    };
  }

  /**
   * Get drawer HTML template
   */
  getDrawerTemplate() {
    return `
      <div class="beezfy-cart__container">
        <div class="beezfy-cart__header">
          <h2 class="beezfy-cart__title">
            <span data-i18n="cart.general.title">Cart</span>
            <span class="beezfy-cart__count" data-cart-count>0</span>
          </h2>
          <button
            class="beezfy-cart__close"
            type="button"
            data-drawer-close
            aria-label="Close cart"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" stroke-width="2"/>
            </svg>
          </button>
        </div>

        <div class="beezfy-cart__body" data-cart-body>
          <div class="beezfy-cart__items" data-cart-items></div>

          <div class="beezfy-cart__empty" data-cart-empty style="display:none;">
            <div class="beezfy-cart__empty-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M3 3h18l-2 10H5L3 3z"/>
                <path d="M5 13v6a2 2 0 002 2h10a2 2 0 002-2v-6"/>
                <circle cx="9" cy="20" r="1"/>
                <circle cx="15" cy="20" r="1"/>
              </svg>
            </div>
            <p class="beezfy-cart__empty-message" data-i18n="cart.general.empty">
              Your cart is empty
            </p>
            <a href="/collections/all" class="beezfy-cart__continue-shopping btn btn--primary">
              <span data-i18n="cart.general.continue_shopping">Continue Shopping</span>
            </a>
          </div>
        </div>

        <div class="beezfy-cart__footer" data-cart-footer style="display:none;">
          <div class="beezfy-cart__subtotal">
            <span data-i18n="cart.general.subtotal">Subtotal</span>
            <span class="beezfy-cart__total" data-cart-total>€0,00</span>
          </div>

          <div class="beezfy-cart__actions">
            <a href="/cart" class="beezfy-cart__view btn btn--secondary">
              <span data-i18n="cart.general.view_cart">View Cart</span>
            </a>
            <button
              type="button"
              class="beezfy-cart__checkout btn btn--primary"
              data-checkout-btn
              onclick="window.location.href='/checkout'"
            >
              <span data-i18n="cart.general.checkout">Checkout</span>
            </button>
          </div>

          <p class="beezfy-cart__note" data-i18n="cart.general.shipping_at_checkout">
            Shipping & taxes calculated at checkout
          </p>
        </div>
      </div>
    `;
  }

  /**
   * Bind all event listeners
   */
  bindEvents() {
    // Cart icon click
    document.addEventListener('click', (e) => {
      // Open cart
      if (e.target.closest(this.selectors.cartIcon)) {
        e.preventDefault();
        this.open();
      }

      // Close cart
      if (e.target.closest(this.selectors.closeBtn)) {
        e.preventDefault();
        this.close();
      }

      // Remove item - check for both old and new selectors
      const removeBtn = e.target.closest('[data-item-remove], [data-action="remove-item"], [data-action="remove"], .bfy-cart-item__remove');
      if (removeBtn) {
        e.preventDefault();
        const key = removeBtn.dataset.itemKey || removeBtn.dataset.key ||
                   removeBtn.closest('[data-item-key]')?.dataset.itemKey ||
                   removeBtn.closest('[data-key]')?.dataset.key;
        if (key) {
          this.removeItem(key);
        }
      }

      // Quantity button - check for increase/decrease actions too
      const qtyBtn = e.target.closest('[data-quantity-btn], [data-action="update-qty"], [data-action="increase"], [data-action="decrease"], .bfy-cart-item__qty-btn');
      if (qtyBtn) {
        e.preventDefault();
        const key = qtyBtn.dataset.itemKey || qtyBtn.dataset.key ||
                   qtyBtn.closest('[data-item-key]')?.dataset.itemKey ||
                   qtyBtn.closest('[data-key]')?.dataset.key;

        // Determine change amount
        let change = 0;
        if (qtyBtn.dataset.change) {
          change = parseInt(qtyBtn.dataset.change);
        } else if (qtyBtn.dataset.action === 'increase') {
          change = 1;
        } else if (qtyBtn.dataset.action === 'decrease') {
          change = -1;
        } else if (qtyBtn.textContent.includes('+')) {
          change = 1;
        } else if (qtyBtn.textContent.includes('-') || qtyBtn.textContent.includes('−')) {
          change = -1;
        }

        if (key && change !== 0) {
          this.updateQuantity(key, change);
        }
      }
    });

    // Overlay click
    if (this.elements.overlay) {
      this.elements.overlay.addEventListener('click', () => this.close());
    }

    // Quantity input change - check for multiple selectors including the actual class
    document.addEventListener('change', (e) => {
      if (e.target.matches('[data-item-quantity], [data-qty-input], .bfy-cart-item__qty-input, .bfy-cart-item__quantity input')) {
        const key = e.target.dataset.itemKey || e.target.dataset.key ||
                   e.target.closest('[data-item-key]')?.dataset.itemKey ||
                   e.target.closest('[data-key]')?.dataset.key;
        const quantity = parseInt(e.target.value);
        if (key && !isNaN(quantity)) {
          this.changeQuantity(key, quantity);
        }
      }
    });

    // Listen for cart events from other systems
    document.addEventListener('cart:add', () => this.refresh());
    document.addEventListener('cart:update', () => this.refresh());
    document.addEventListener('product:added', () => {
      this.refresh();
      // Apri il drawer solo se l'azione post-ATC è "drawer" (altrimenti gestisce il popup premium)
      const action = (window.BeezfyTheme && window.BeezfyTheme.settings && window.BeezfyTheme.settings.atc_action_type) || 'popup';
      if (action === 'drawer') this.open();
    });

    // ESC key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.state.isOpen) {
        this.close();
      }
    });
  }

  /**
   * Fetch cart data from Shopify
   */
  async fetchCart() {
    try {
      const response = await fetch(this.config.routes.cart, {
        headers: { 'Accept': 'application/json' }
      });

      if (!response.ok) throw new Error('Failed to fetch cart');

      this.state.cart = await response.json();

      // Always update UI after fetching
      this.updateUI();

      // Also update header specifically
      this.updateHeaderCart();

      return this.state.cart;
    } catch (error) {
      return null;
    }
  }

  /**
   * Update header cart count specifically
   */
  updateHeaderCart() {
    if (!this.state.cart) return;

    // Find all possible header cart count elements
    const selectors = [
      '.action-cart .cart-count',
      '.cart-count',
      '.header__icon--cart .cart-count-bubble',
      '.header__icon--cart .cart-count',
      'a[href="/cart"] .cart-count-bubble',
      'a[href="/cart"] .cart-count',
      '.site-header .cart-count-bubble',
      '.site-header .cart-count',
      '[data-drawer-trigger="cart"] .cart-count'
    ];

    // First try to find existing cart count elements
    let found = false;
    selectors.forEach(selector => {
      const elements = document.querySelectorAll(selector);
      if (elements.length > 0) {
        found = true;
        elements.forEach(el => {
          el.textContent = this.state.cart.item_count;

          // Show/hide based on count
          if (this.state.cart.item_count > 0) {
            el.style.display = '';
            el.classList.remove('hidden', 'hide');
          } else {
            el.style.display = 'none';
          }
        });
      }
    });

    // If no cart count exists and we have items, create one
    if (!found && this.state.cart.item_count > 0) {
      const cartButton = document.querySelector('[data-drawer-trigger="cart"], .action-cart');
      if (cartButton) {
        const existingCount = cartButton.querySelector('.cart-count');
        if (!existingCount) {
          const countEl = document.createElement('span');
          countEl.className = 'cart-count';
          countEl.textContent = this.state.cart.item_count;
          cartButton.appendChild(countEl);
        }
      }
    }
  }

  /**
   * Update cart UI with latest data
   */
  updateUI() {
    if (!this.state.cart) return;

    // Update all cart counts - use broader selector
    const countElements = document.querySelectorAll('[data-cart-count], .cart-count, .cart-count-bubble, .bfy-cart__count');
    countElements.forEach(el => {
      el.textContent = this.state.cart.item_count;

      // Show/hide count bubble
      if (this.state.cart.item_count > 0) {
        el.style.display = '';
        // Also remove any hide classes
        el.classList.remove('hidden');
      } else if (el.classList.contains('cart-count-bubble')) {
        el.style.display = 'none';
      }
    });

    // Force update header cart count
    const headerIcon = document.querySelector('.header__icon--cart');
    if (headerIcon) {
      const bubble = headerIcon.querySelector('.cart-count-bubble, .cart-count');
      if (bubble) {
        bubble.textContent = this.state.cart.item_count;
        bubble.style.display = this.state.cart.item_count > 0 ? '' : 'none';
      }
    }

    // Update drawer content - force refresh
    this.renderCartItems();

    // Update drawer if it exists and is open
    if (this.elements.drawer) {
      const drawerCount = this.elements.drawer.querySelector('[data-cart-count]');
      if (drawerCount) {
        drawerCount.textContent = this.state.cart.item_count;
      }
    }
  }

  /**
   * Render cart items in drawer
   */
  renderCartItems() {
    const { cart } = this.state;

    // Find elements in drawer
    const drawer = document.querySelector('#beezfy-cart-drawer');
    if (!drawer) {
      return;
    }

    const itemsContainer = drawer.querySelector('[data-cart-items]');
    const footer = drawer.querySelector('.bfy-cart__footer');
    const emptyState = drawer.querySelector('.bfy-cart__empty');

    if (!cart || cart.item_count === 0) {
      // Show empty state
      if (itemsContainer) {
        itemsContainer.style.display = 'none';
        itemsContainer.innerHTML = '';
      }
      if (footer) footer.style.display = 'none';

      // Create or update empty state in content area
      const content = drawer.querySelector('.bfy-cart__content, [data-cart-content]');
      if (content) {
        let existingEmpty = content.querySelector('.bfy-cart__empty');

        // Remove items container from view completely
        const itemsDiv = content.querySelector('[data-cart-items]');
        if (itemsDiv) itemsDiv.style.display = 'none';

        if (!existingEmpty) {
          // Create empty state HTML
          const emptyDiv = document.createElement('div');
          emptyDiv.className = 'bfy-cart__empty';
          emptyDiv.innerHTML = `
            <div class="bfy-cart__empty-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                <path d="M3 3H21L19 13H5L3 3Z"/>
                <path d="M3 3L5 13H19L21 3"/>
                <circle cx="9" cy="20" r="1"/>
                <circle cx="15" cy="20" r="1"/>
              </svg>
            </div>
            <h3 class="bfy-cart__empty-title">Il tuo carrello è vuoto</h3>
            <p class="bfy-cart__empty-text">Aggiungi prodotti per continuare</p>
            <a href="/collections/all" class="bfy-cart__button bfy-cart__button--primary">
              Continua lo shopping
            </a>
          `;
          // Insert empty state before recommendations
          const recommendations = content.querySelector('.bfy-cart__recommendations');
          if (recommendations) {
            content.insertBefore(emptyDiv, recommendations);
          } else {
            content.appendChild(emptyDiv);
          }
        } else {
          // Show existing empty state
          existingEmpty.style.display = 'block';
        }

        // Keep recommendations visible even with empty cart
        const recommendations = content.querySelector('.bfy-cart__recommendations');
        if (recommendations) {
          recommendations.style.display = 'block';
        }
      }
      return;
    }

    // Hide empty state and show items
    const allEmptyStates = drawer.querySelectorAll('.bfy-cart__empty');
    allEmptyStates.forEach(el => el.style.display = 'none');
    if (itemsContainer) {
      itemsContainer.style.display = 'block';
      // Clear and re-render all items
      itemsContainer.innerHTML = cart.items.map(item => this.getItemTemplate(item)).join('');
    }
    if (footer) footer.style.display = 'block';

    // Update footer totals
    if (footer) {
      const subtotal = footer.querySelector('[data-cart-subtotal]');
      if (subtotal) {
        subtotal.textContent = this.formatMoney(cart.total_price);
      }

      // Update shipping bar if present
      const shippingBar = footer.querySelector('.bfy-cart__shipping-bar');
      if (shippingBar && window.theme?.settings?.bfy_cart_free_shipping_threshold) {
        const threshold = window.theme.settings.bfy_cart_free_shipping_threshold * 100;
        const remaining = threshold - cart.total_price;
        const progressBar = shippingBar.querySelector('[data-progress-fill]');

        if (remaining > 0) {
          const progress = (cart.total_price / threshold) * 100;
          if (progressBar) progressBar.style.width = progress + '%';
        } else {
          if (progressBar) progressBar.style.width = '100%';
        }
      }
    }
  }

  /**
   * Get cart item HTML template
   */
  getItemTemplate(item) {
    // Use same structure as Liquid template
    return `
      <div class="bfy-cart-item" data-item-key="${item.key}" data-item-id="${item.id}">
        <a href="${item.url}" class="bfy-cart-item__image">
          <img
            src="${window.bfyImg(item.image, 240)}"
            alt="${item.product_title}"
            width="72"
            height="90"
            loading="lazy"
            class="bfy-cart-item__img"
          >
        </a>

        <div class="bfy-cart-item__info">
          <a href="${item.url}" class="bfy-cart-item__title">
            ${item.product_title}
          </a>
          ${item.variant_title ? `
            <div class="bfy-cart-item__variant">${item.variant_title}</div>
          ` : ''}

          <div class="bfy-cart-item__price-row">
            <div class="bfy-cart-item__prices">
              <span class="bfy-cart-item__price">
                ${this.formatMoney(item.price)}
              </span>
            </div>

            <div class="bfy-cart-item__qty">
              <button
                class="bfy-cart-item__qty-btn"
                type="button"
                data-action="decrease"
                data-item-key="${item.key}"
                aria-label="Decrease quantity"
              >
                −
              </button>

              <input
                type="number"
                class="bfy-cart-item__qty-input"
                value="${item.quantity}"
                min="0"
                data-item-key="${item.key}"
                aria-label="Quantity"
                readonly
              >

              <button
                class="bfy-cart-item__qty-btn"
                type="button"
                data-action="increase"
                data-item-key="${item.key}"
                aria-label="Increase quantity"
              >
                +
              </button>
            </div>
          </div>
        </div>

        <button
          class="bfy-cart-item__remove"
          type="button"
          data-action="remove"
          data-item-key="${item.key}"
          aria-label="Remove ${item.product_title}"
          title="Remove"
          style="position: absolute; top: 2px; right: 2px; width: 24px; height: 24px; border: none; background: transparent; padding: 2px; display: flex; align-items: center; justify-content: center; cursor: pointer; z-index: 10; transition: all 0.2s;"
          onmouseover="this.querySelector('svg').style.stroke='#dc2626'; this.style.transform='scale(1.15)'"
          onmouseout="this.querySelector('svg').style.stroke='#6b7280'; this.style.transform='scale(1)'"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6b7280" stroke-width="2">
            <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14zM10 11v6M14 11v6"/>
          </svg>
        </button>
      </div>
    `;
  }

  /**
   * Add item to cart
   */
  async addToCart(variantId, quantity = 1, properties = {}) {
    if (this.state.isLoading) return;
    this.state.isLoading = true;

    try {
      const response = await fetch(this.config.routes.add, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: variantId,
          quantity,
          properties
        })
      });

      if (!response.ok) throw new Error('Failed to add to cart');

      const item = await response.json();

      // Refresh cart and wait for it
      await this.fetchCart();

      // Force UI update
      this.updateUI();

      // Force header update
      this.updateHeaderCart();

      // Dispatch event
      document.dispatchEvent(new CustomEvent('beezfy:cart:added', {
        detail: { item, cart: this.state.cart }
      }));

      return item;
    } catch (error) {
      throw error;
    } finally {
      this.state.isLoading = false;
    }
  }

  /**
   * Update item quantity
   */
  async updateQuantity(key, change) {
    const item = this.state.cart.items.find(i => i.key === key);
    if (!item) return;

    const newQuantity = Math.max(0, item.quantity + change);
    await this.changeQuantity(key, newQuantity);
  }

  /**
   * Change item quantity
   */
  async changeQuantity(key, quantity) {
    if (this.state.isLoading) return;
    this.state.isLoading = true;

    try {
      const response = await fetch(this.config.routes.change, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: key,
          quantity
        })
      });

      if (!response.ok) throw new Error('Failed to update quantity');

      this.state.cart = await response.json();

      // Force complete UI refresh
      this.updateUI();
      this.renderCartItems();

      // If quantity is 0, item was removed
      if (quantity === 0) {
      }

      // Dispatch event
      document.dispatchEvent(new CustomEvent('beezfy:cart:updated', {
        detail: { cart: this.state.cart }
      }));

    } catch (error) {
    } finally {
      this.state.isLoading = false;
    }
  }

  /**
   * Remove item from cart
   */
  async removeItem(key) {
    await this.changeQuantity(key, 0);
  }

  /**
   * Open cart drawer
   */
  async open() {
    if (this.state.isOpen) return;

    // Add classes
    this.elements.drawer.classList.add('is-open');
    this.elements.overlay.classList.add('is-active');
    document.body.classList.add('drawer-open');

    // Update aria
    this.elements.drawer.setAttribute('aria-hidden', 'false');

    // Set state
    this.state.isOpen = true;

    // Start loading recommendations immediately (don't wait for cart)
    const recommendationsPromise = this.loadRecommendations();

    // Only refresh if cart is stale or not loaded
    if (!this.state.cart) {
      await this.fetchCart();
    } else {
      // Just update UI with existing cart data
      this.updateUI();
      this.renderCartItems();
    }

    // Wait for recommendations to complete
    await recommendationsPromise;

    // Dispatch event
    document.dispatchEvent(new CustomEvent('beezfy:cart:opened'));
  }

  /**
   * Close cart drawer
   */
  close() {
    if (!this.state.isOpen) return;

    // Remove classes
    this.elements.drawer.classList.remove('is-open');
    this.elements.overlay.classList.remove('is-active');
    document.body.classList.remove('drawer-open');

    // Update aria
    this.elements.drawer.setAttribute('aria-hidden', 'true');

    // Set state
    this.state.isOpen = false;

    // Dispatch event
    document.dispatchEvent(new CustomEvent('beezfy:cart:closed'));
  }

  /**
   * Refresh cart with debounce
   */
  refresh() {
    clearTimeout(this.state.refreshTimer);
    this.state.refreshTimer = setTimeout(async () => {
      await this.fetchCart();
      this.updateUI();
      this.renderCartItems();
      // Reload recommendations if cart is open
      if (this.state.isOpen) {
        await this.loadRecommendations();
      }
    }, this.config.debounceDelay);
  }

  /**
   * Load product recommendations
   */
  async loadRecommendations() {
    const container = document.querySelector('[data-recommendations-grid]');
    if (!container) {
      return;
    }

    try {
      // Show loading state
      container.innerHTML = '<div class="bfy-cart__reco-loading">Caricamento suggerimenti...</div>';

      // If cart hasn't loaded yet, fetch it first
      if (!this.state.cart) {
        await this.fetchCart();
      }

      // Get first product ID from cart
      let productId = null;
      if (this.state.cart && this.state.cart.items && this.state.cart.items.length > 0) {
        productId = this.state.cart.items[0].product_id;
      }

      let products = [];

      // If we have a product in cart, try to get recommendations
      if (productId) {
        try {
          const recResponse = await fetch(`/recommendations/products.json?product_id=${productId}&limit=4`);
          if (recResponse.ok) {
            const recData = await recResponse.json();
            if (recData.products && recData.products.length > 0) {
              products = recData.products;
            }
          }
        } catch (e) {
          // Silently fail and try collections
        }
      }

      // Always try to get products from collections if none found or cart is empty
      if (products.length === 0) {
        // Check admin setting for fallback type (default to 'latest')
        const fallbackType = window.theme?.settings?.bfy_cart_fallback_products || 'latest';

        // Define collection URLs based on fallback type
        let collectionsToTry = [];

        switch(fallbackType) {
          case 'best-sellers':
            collectionsToTry = [
              '/collections/best-sellers/products.json?limit=4',
              '/collections/all/products.json?limit=4&sort_by=best-selling',
              '/products.json?limit=4'
            ];
            break;
          case 'random':
            collectionsToTry = [
              '/collections/all/products.json?limit=50', // Get more then pick random 4
              '/products.json?limit=50'
            ];
            break;
          case 'featured':
            collectionsToTry = [
              '/collections/featured/products.json?limit=4',
              '/collections/frontpage/products.json?limit=4',
              '/collections/all/products.json?limit=4'
            ];
            break;
          case 'latest':
          default:
            // Default to latest products (newest first)
            collectionsToTry = [
              '/collections/all/products.json?limit=4&sort_by=created-descending',
              '/products.json?limit=4&sort_by=created_at&order=desc',
              '/collections/all/products.json?limit=4'
            ];
            break;
        }

        // Always add a final fallback to any products
        collectionsToTry.push('/products.json?limit=4');

        for (const url of collectionsToTry) {
          try {
            const response = await fetch(url);

            if (response.ok) {
              const data = await response.json();
              let productsArray = data.products || data;

              if (Array.isArray(productsArray) && productsArray.length > 0) {
                // If random mode and we got many products, pick 4 random ones
                if (fallbackType === 'random' && productsArray.length > 4) {
                  const shuffled = [...productsArray].sort(() => Math.random() - 0.5);
                  products = shuffled.slice(0, 4);
                } else {
                  products = productsArray.slice(0, 4);
                }
                break; // Stop once we have products
              }
            }
          } catch (e) {
            // Silently continue to next collection
          }
        }

        // If still no products, try one more time with the most basic request
        if (products.length === 0) {
          try {
            const lastResponse = await fetch('/products.json');
            if (lastResponse.ok) {
              const lastData = await lastResponse.json();
              const lastProducts = lastData.products || lastData;
              if (Array.isArray(lastProducts) && lastProducts.length > 0) {
                // Sort by created_at if available to get latest
                if (lastProducts[0].created_at) {
                  lastProducts.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
                }
                products = lastProducts.slice(0, 4);
              }
            }
          } catch (e) {
            // Final attempt failed silently
          }
        }
      }

      // Render products
      if (products.length > 0) {
        const html = products.map(product => {
          // Get the proper image URL with Shopify object safety
          let imageUrl = '';
          const imageCandidates = [
            product?.featured_image,
            product?.featured_image?.src,
            product?.images?.[0],
            product?.images?.[0]?.src,
            product?.image,
            product?.image?.src
          ];

          for (const candidate of imageCandidates) {
            if (typeof candidate === 'string' && candidate.trim()) {
              imageUrl = candidate;
              break;
            }
            if (candidate && typeof candidate === 'object' && typeof candidate.src === 'string' && candidate.src.trim()) {
              imageUrl = candidate.src;
              break;
            }
          }

          // Ensure image URL has proper size for small thumbnails
          if (typeof imageUrl === 'string' && imageUrl.length && !imageUrl.includes('_100x.')) {
            imageUrl = imageUrl.replace(/\.(jpg|jpeg|png|gif|webp)(\?.*)?$/i, '_100x.$1$2');
          }

          // Get proper product URL
          const productUrl = product.url || `/products/${product.handle}`;

          // Get price - normalize to cents across data shapes
          const price = this.getProductPriceInCents(product);

          // Horizontal card layout with image on left, text on right
          return `
            <div class="bfy-cart__reco-item">
              <a href="${productUrl}" class="bfy-cart__reco-link">
                ${imageUrl ? `<img src="${imageUrl}" alt="${product.title}" class="bfy-cart__reco-image" loading="lazy">` : '<div class="bfy-cart__reco-image"></div>'}
                <div class="bfy-cart__reco-info">
                  <h4 class="bfy-cart__reco-title" title="${product.title}">${product.title}</h4>
                  ${price !== null ? `<p class="bfy-cart__reco-price">${this.formatMoney(price)}</p>` : ''}
                </div>
              </a>
            </div>
          `;
        }).join('');

        container.innerHTML = html;
        // Don't set loaded flag - we want to refresh on each cart open
      } else {
        // If still no products, show a friendly message
        container.innerHTML = '<p style="text-align: center; color: #999; font-size: 12px; padding: 20px;">Scopri i nostri prodotti nel <a href="/collections/all" style="color: #000; text-decoration: underline;">catalogo completo</a></p>';
      }
    } catch (error) {
      // Even on error, show a helpful link instead of error message
      container.innerHTML = '<p style="text-align: center; color: #999; font-size: 12px; padding: 20px;">Esplora il nostro <a href="/collections/all" style="color: #000; text-decoration: underline;">catalogo prodotti</a></p>';
    }
  }

  /**
   * Normalize product price into cents regardless of source shape
   */
  getProductPriceInCents(product) {
    const parseCandidate = (value) => {
      if (value === null || value === undefined) return null;

      if (typeof value === 'number') {
        if (!Number.isFinite(value) || value <= 0) return null;
        return value >= 1000 ? Math.round(value) : Math.round(value * 100);
      }

      if (typeof value === 'string') {
        const normalized = value
          .replace(/,/g, '.')
          .replace(/[^0-9.]/g, '');
        if (!normalized) return null;
        const numeric = parseFloat(normalized);
        if (Number.isNaN(numeric) || numeric <= 0) return null;
        return numeric >= 1000 ? Math.round(numeric) : Math.round(numeric * 100);
      }

      if (typeof value === 'object') {
        if (typeof value.amount !== 'undefined') {
          return parseCandidate(value.amount);
        }
        if (typeof value.price !== 'undefined') {
          return parseCandidate(value.price);
        }
      }

      return null;
    };

    const candidates = [
      product?.price,
      product?.final_price,
      product?.min_price,
      product?.price_min,
      product?.price_max,
      product?.variants?.[0]?.price,
      product?.variants?.[0]?.final_price,
      product?.variants?.[0]?.price_min,
      product?.variants?.[0]?.price_max,
      product?.variants?.[0]?.compare_at_price,
      product?.variants?.[0]?.priceV2,
      product?.variants?.[0]?.presentment_prices?.[0]?.price,
      product?.variants?.[0]?.presentment_prices?.[0]?.price?.amount
    ];

    for (const candidate of candidates) {
      const cents = parseCandidate(candidate);
      if (typeof cents === 'number') {
        return cents;
      }
    }

    return null;
  }

  /**
   * Format money
   */
  formatMoney(cents) {
    const money = (cents / 100).toFixed(2);
    const moneyWithComma = money.replace('.', ',');

    // Try different format patterns
    if (window.theme?.moneyFormat) {
      return window.theme.moneyFormat
        .replace('{{amount}}', money)
        .replace('{{amount_with_comma_separator}}', moneyWithComma)
        .replace('{{amount_no_decimals}}', Math.floor(cents / 100));
    }

    // Default to Euro format with comma
    return `€${moneyWithComma}`;
  }

  /**
   * Public API
   */
  getCart() {
    return this.state.cart;
  }

  isCartOpen() {
    return this.state.isOpen;
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.BeezfyCart = new BeezfyPremiumCart();
  });
} else {
  window.BeezfyCart = new BeezfyPremiumCart();
}
